import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./style.module.css";

// Call session modal from the Claude Design file "OTP & Package Modals.dc.html".
// Success follows the design; loading and error use the same card.
// status: "loading" | "success" | "error". Centered card; bottom sheet below 560px.

// "+918035303702" / "08035303702" / "8035303702" -> "+91 80353 03702"; anything else as sent.
export const formatSessionNumber = (value) => {
  const raw = String(value || "").trim();
  let d = raw.replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d.length === 10 ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : raw;
};
const telHref = (value) => `tel:${String(value || "").replace(/[^\d+]/g, "")}`;

const CallSessionSheet = ({ status, number, error, loadingText, riderName, onClose, onRetry, onCallNow, onCopy }) => {
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);
  const closeFn = useRef(onClose);
  closeFn.current = onClose;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeFn.current && closeFn.current();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKey);
      clearTimeout(copyTimer.current);
    };
  }, []);

  const copy = async () => {
    onCopy && onCopy();
    try {
      await navigator.clipboard.writeText(String(number || "").replace(/\s/g, ""));
    } catch (e) {
      // Clipboard can be blocked; the label still confirms the tap.
    }
    setCopied(true);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1800);
  };

  const label =
    status === "success" ? "Call Session Created Successfully" : status === "error" ? "Couldn't start the call" : "Connecting your call";

  return createPortal(
    <div className={styles.wrap}>
      <div className={styles.overlay} onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={label} className={styles.sheet}>
        <span className={styles.grab} />
        <button type="button" aria-label="Close" className={styles.close} onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div className={styles.body} aria-live="polite">
          {status === "loading" && (
            <>
              <span className={`${styles.badge} ${styles.badgeSoft}`}>
                <span className={styles.spinner} />
              </span>
              <div className={styles.copyBlock}>
                <h2 className={styles.title}>Connecting your call</h2>
                <p className={styles.text}>{loadingText || "Creating a secure call line to your Kaptaan…"}</p>
              </div>
            </>
          )}

          {status === "success" && (
            <>
              <span className={`${styles.badge} ${styles.badgeOk}`}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path className={styles.draw} d="M5 12.5l4.5 4.5L19 7.5" strokeDasharray="60" />
                </svg>
              </span>
              <h2 className={styles.title}>Call Session Created Successfully</h2>
              <div className={styles.actions}>
                <div className={styles.numberRow}>
                  <span className={styles.number}>{formatSessionNumber(number)}</span>
                  <button type="button" aria-label="Copy number" className={`${styles.copy} ${copied ? styles.copied : ""}`} onClick={copy}>
                    {copied ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M5 12.5l4.5 4.5L19 7.5" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="9" y="9" width="11" height="11" rx="2" />
                        <path d="M5 15V5a1 1 0 011-1h10" />
                      </svg>
                    )}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <a href={telHref(number)} className={styles.primary} onClick={onCallNow}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2z" />
                  </svg>
                  Call Now
                </a>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <span className={`${styles.badge} ${styles.badgeError}`}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2z" />
                  <path d="M3 3l18 18" />
                </svg>
              </span>
              <div className={styles.copyBlock}>
                <h2 className={styles.title}>Couldn't start the call</h2>
                <p className={styles.text}>
                  {error || `We couldn't connect you to ${riderName ? riderName.split(" ")[0] : "your Kaptaan"} right now. Please try again in a moment.`}
                </p>
              </div>
              <div className={styles.actions}>
                {onRetry && (
                  <button type="button" className={styles.primary} onClick={onRetry}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 11a8 8 0 10-2.3 5.7" />
                      <path d="M20 5v6h-6" />
                    </svg>
                    Try again
                  </button>
                )}
                <button type="button" className={styles.secondary} onClick={onClose}>
                  Close
                </button>
                <span className={styles.help}>
                  Still facing issues? Call Blitz support at{" "}
                  <a href="tel:+918035316943" className={styles.helpLink}>
                    +91 8035316943
                  </a>
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CallSessionSheet;
